import {
  BaseApiService,
  type JsonApiResponse,
  type ServiceOptions,
} from './base.service';

export interface Plan {
  id: string;
  type: string;
  attributes: {
    name: string;
    price: number;
    currency: string;
    interval: string;
    features: string[];
    limits: Record<string, unknown>;
    monthly_generations_limit?: number;
  };
}

export interface Subscription {
  id: string;
  type: string;
  attributes: {
    status: string;
    current_period_start: string;
    current_period_end: string;
    cancel_at_period_end: boolean;
    plan_id: string;
    plan?: Plan;
  };
}

export class SubscriptionsService extends BaseApiService {
  async getPublicPlans(options?: ServiceOptions): Promise<Plan[]> {
    const response = await this.httpGet<JsonApiResponse<Plan[]>>(
      '/v1/plans/public',
      undefined,
      {
        loading: { trackEndpoint: 'plans-public' },
        ...options,
      }
    );
    return response.data;
  }

  async getPlans(
    page?: number,
    perPage?: number,
    options?: ServiceOptions
  ): Promise<{ data: Plan[]; meta?: Record<string, unknown> }> {
    const queryParams = {
      'page[number]': page,
      'page[size]': perPage,
    };
    const queryString = this.buildQueryString(queryParams);
    const url = `/v1/plans${queryString ? `?${queryString}` : ''}`;

    const response = await this.httpGet<JsonApiResponse<Plan[]>>(url, undefined, {
      loading: { trackEndpoint: 'plans-list' },
      ...options,
    });
    return { data: response.data, meta: response.meta };
  }

  async getPlan(planId: string, options?: ServiceOptions): Promise<Plan> {
    const response = await this.httpGet<JsonApiResponse<Plan>>(
      `/v1/plans/${planId}`,
      undefined,
      {
        loading: { trackEndpoint: `plans-get-${planId}` },
        ...options,
      }
    );
    return response.data;
  }

  async getSubscriptions(options?: ServiceOptions): Promise<Subscription[]> {
    const response = await this.httpGet<JsonApiResponse<Subscription[]>>(
      '/v1/subscriptions',
      undefined,
      {
        loading: { trackEndpoint: 'subscriptions-list' },
        ...options,
      }
    );
    return response.data;
  }

  async getSubscription(
    subscriptionId: string,
    options?: ServiceOptions
  ): Promise<Subscription> {
    const response = await this.httpGet<JsonApiResponse<Subscription>>(
      `/v1/subscriptions/${subscriptionId}`,
      undefined,
      {
        loading: { trackEndpoint: `subscriptions-get-${subscriptionId}` },
        ...options,
      }
    );
    return response.data;
  }

  async createSubscription(
    planId: string,
    options?: ServiceOptions
  ): Promise<Subscription> {
    const requestData = this.formatJsonApiData('subscription', {
      plan_id: planId,
    });

    const response = await this.post<JsonApiResponse<Subscription>>(
      '/v1/subscriptions',
      requestData,
      undefined,
      {
        loading: { trackEndpoint: 'subscriptions-create' },
        ...options,
      }
    );
    return response.data;
  }

  async cancelSubscription(
    subscriptionId: string,
    immediately: boolean = false,
    options?: ServiceOptions
  ): Promise<void> {
    const queryString = this.buildQueryString({
      immediately: immediately || undefined,
    });
    const url = `/v1/subscriptions/${subscriptionId}${queryString ? `?${queryString}` : ''}`;

    await this.httpDelete(url, undefined, {
      loading: { trackEndpoint: `subscriptions-cancel-${subscriptionId}` },
      ...options,
    });
  }

  async resumeSubscription(
    subscriptionId: string,
    options?: ServiceOptions
  ): Promise<Subscription> {
    const response = await this.post<JsonApiResponse<Subscription>>(
      `/v1/subscriptions/${subscriptionId}/resume`,
      {},
      undefined,
      {
        loading: { trackEndpoint: `subscriptions-resume-${subscriptionId}` },
        ...options,
      }
    );
    return response.data;
  }
}

export const subscriptionsService = new SubscriptionsService();
